/**
 * 保单台账契约（**B 域 · B5** · §5.5.5）
 *
 * 依据：B 域 PRD **§5.5.5**（保单台账字段表 · 被保人 1:N）· `DR-008` §6.1/§7 ·
 * `DR-016` **§6.9**（承保状态 6 态）· 架构 **§6.1**（Zod 唯一契约源）。
 *
 * ## 🔴 三条口径要点
 *
 * 1. **金额一律「整数分」**（规格 §22.1 禁浮点）—— 保额 / 现金价值 / 年缴保费都是；
 * 2. 🔴 **保额不计净资产、现金价值计入**（限 `active`/`paid_up`）——
 *    判定唯一实现在 `nucleus/insurance.ts` 的 `cashValueCountsInNetWorth`；
 * 3. 🔴 **被保人是 1:N**（`policy_insureds` 关联表，**P0 即建**）——
 *    投保人 / 受益人是 1:1 字段（`DR-008`：P0 无多投保人 / 多受益人诉求，**不预留**）。
 *
 * ## ⚠️ 「三人身份」的载体（2026-09-30 拍板）
 *
 * `policyholder` / `beneficiary` / `policy_insureds.insuredId` 一律**文本单列**，
 * 语义 = **「id 优先、姓名兜底」**（`resolveParty` 是唯一判定处）：
 * 填系统用户 id（`usr_…`）按 id 解析、否则按姓名。**不加第二个列**（避免同一事实两份载体）。
 */

import { z } from 'zod';
import { POLICY_STATUSES, PolicyStatus } from '../enums/account.js';
import { InsurancePolicy } from '../constants/index.js';

/** `YYYY-MM-DD`（账本本地日历日 —— 与交易日同口径，无时区） */
const DayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式必须是 YYYY-MM-DD（账本本地日历日）');

/** 金额（整数分、非负）—— 🔴 保额 / 现金价值 / 年缴保费都用它 */
const CentsAmount = z
  .number()
  .int('金额必须是整数分（禁浮点）')
  .min(0, '金额不得为负数')
  .max(InsurancePolicy.MaxPolicyAmount, '金额超出防御上限（疑似把「元」当「分」填了）');

/** 当事人（投保人 / 受益人 / 被保人）—— 文本单列，id 优先姓名兜底 */
const PartyText = z
  .string()
  .trim()
  .min(1, '当事人不得为空')
  .max(InsurancePolicy.PartyMaxLength, `当事人长度上限 ${InsurancePolicy.PartyMaxLength}`);

/** 承保状态（6 态） */
const StatusEnum = z.enum(POLICY_STATUSES as unknown as [PolicyStatus, ...PolicyStatus[]]);

// ─────────────────────────── 建保单 ───────────────────────────

/**
 * `POST /api/insurance/policies` —— 建一张保单。
 *
 * 🔴 **必填 = B 域 PRD §5.5.5 的清单**：保单号 / 保险公司 / 险种 /
 * **投保人 / 被保人 / 受益人三人分离** / 保险期间 / 保额 / 现金价值 / 缴费方式 / 年缴保费 / 承保状态。
 * 其中**被保人以数组提交**（1:N，P0 UI 只露 1 个，但契约**从一开始就是数组** —— 见 `DR-008` P4：
 * 存单个 `insuredId` 到 P1 需改表 + 迁移，成本远高于现在多一张关联表）。
 */
export const CreatePolicyRequestSchema = z.object({
  /** 保单号 —— **账本内唯一**（服务端校验，冲突 409） */
  policyNo: z.string().trim().min(1, '保单号必填').max(InsurancePolicy.PolicyNoMaxLength),
  /** 保险公司 —— **保单台账的分组键**（商业保险按公司名分组） */
  company: z.string().trim().min(1, '保险公司必填').max(InsurancePolicy.CompanyMaxLength),
  /** 险种（重疾 / 医疗 / 意外 / 寿险…）—— 自由文本，不做枚举（`DR-016` 未定取值集） */
  insuranceType: z.string().trim().min(1, '险种必填').max(InsurancePolicy.InsuranceTypeMaxLength),
  /** 投保人（1:1） */
  policyholder: PartyText,
  /** 🔴 **被保人（1:N）** —— 至少 1 个 */
  insureds: z.array(PartyText).min(1, '至少 1 个被保人').max(InsurancePolicy.MaxInsureds, `被保人上限 ${InsurancePolicy.MaxInsureds}`),
  /** 受益人（1:1） */
  beneficiary: PartyText,
  /** 保险期间起 */
  startDate: DayString,
  /** 保险期间止（长期险可缺省 = 未填） */
  endDate: DayString.nullish(),
  /** 保额 —— 🔴 **不计净资产** */
  sumAssured: CentsAmount,
  /** 现金价值 —— 🔴 **计入净资产**（限 `active`/`paid_up`） */
  cashValue: CentsAmount,
  /** 缴费方式（趸交 / 年交 / 月交） */
  paymentMode: z.string().trim().min(1, '缴费方式必填').max(InsurancePolicy.PaymentModeMaxLength),
  /** 年缴保费 */
  annualPremium: CentsAmount,
  /** 承保状态；缺省 `active` */
  status: StatusEnum.optional(),
});
export type CreatePolicyRequest = z.infer<typeof CreatePolicyRequestSchema>;

/**
 * `PATCH /api/insurance/policies/:id` —— 改一张保单。
 *
 * 🔴 字段语义是**整字段替换**，不是"打补丁"（与 C 域流水编辑同约定）。
 * ⚠️ **`policyNo` 可改**（保单号录错是真实场景），但改了要重新过账本内唯一校验。
 * ⚠️ **被保人传了就是整体替换**（与腿集合同约定），未传则不动。
 */
export const UpdatePolicyRequestSchema = CreatePolicyRequestSchema.partial();
export type UpdatePolicyRequest = z.infer<typeof UpdatePolicyRequestSchema>;

/**
 * 列表查询（分组与筛选）。
 *
 * ## 🔴 `statuses` 为什么是**逗号分隔字符串**而不是数组
 *
 * 查询串里的"数组参数"**没有标准编码**，各客户端序列化方式互不相同。实测三种常见写法
 * 在本契约（`z.object()`）下的结局**都不好**：
 *
 * | 写法 | 实测结果 |
 * |---|---|
 * | `?statuses=lapsed` | **400**（契约要数组、收到字符串） |
 * | `?statuses[]=lapsed` | 🔴 键名成了 `statuses[]` ⇒ 被 Zod **当未知键剥掉** ⇒ 过滤条件**静默消失**（返回全部） |
 * | `?statuses=lapsed&statuses=active` | 同上被剥掉 ⇒ **静默返回全部** |
 *
 * 🔴 **"静默返回全部"正是本项目明令禁止的失败方向**（`AC-C1.21` 禁静默失败）——
 * 用户以为筛了"只看失效"，却拿到全部且**毫无提示**。⇒ 改为**逗号分隔单值**：
 * 编码唯一、各客户端都好写，且**非法取值会被枚举校验挡下**（400，而不是静默忽略）。
 *
 * ⚠️ 端侧拼串：`statuses=active,paid_up`；空串 = 未筛选。
 */
export const PolicyQuerySchema = z.object({
  /** 按保险公司筛选（保单台账按公司分组） */
  company: z.string().trim().min(1).optional(),
  /**
   * 按承保状态筛选 —— **逗号分隔**（如 `statuses=active,paid_up`）。
   * 每个取值都过枚举校验：非法值 ⇒ **400**（不静默忽略）。
   */
  statuses: z
    .string()
    .trim()
    .optional()
    .transform((raw) =>
      raw === undefined || raw === ''
        ? undefined
        : raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s !== ''),
    )
    .pipe(z.array(StatusEnum).optional()),
  /** **只算入净资产的**（`active`/`paid_up`）—— 供净资产侧取数用，端侧一般不用 */
  countedOnly: z.coerce.boolean().optional(),
});
export type PolicyQuery = z.infer<typeof PolicyQuerySchema>;

// ─────────────────────────── 视图 ───────────────────────────

/** 当事人视图（端侧零判定：`kind` 由服务端算好下发） */
export interface PolicyPartyView {
  /** `'user'` = 存的是系统用户 id（服务端已 join 出 `name`）；`'name'` = 直接存的姓名 */
  kind: 'user' | 'name';
  /** 系统用户 id（`kind='user'` 时非空） */
  userId: string | null;
  /** 展示名（`kind='user'` 时由服务端 join 出；否则就是存的姓名） */
  name: string;
}

/** 保单视图（端侧零 join） */
export interface PolicyView {
  id: string;
  policyNo: string;
  /** 分组键 */
  company: string;
  insuranceType: string;
  policyholder: PolicyPartyView;
  /** 🔴 1:N —— 首版 UI 只露 1 个，契约已经是数组 */
  insureds: PolicyPartyView[];
  beneficiary: PolicyPartyView;
  startDate: string;
  endDate: string | null;
  /** 保额（整数分）—— 🔴 **不计净资产** */
  sumAssured: number;
  /** 现金价值（整数分）—— 🔴 **计入净资产**（限 `active`/`paid_up`） */
  cashValue: number;
  paymentMode: string;
  annualPremium: number;
  status: PolicyStatus;
  /** 🔴 **本保单的现金价值此刻是否计入净资产** —— 服务端按 `cashValueCountsInNetWorth()` 算好下发，端侧**不自行判定** */
  cashValueCounted: boolean;
  createdAt: number;
  updatedAt: number;
}

/**
 * 删除结果（`DELETE /api/insurance/policies/:id`）。
 *
 * 📌 它**原本只声明在服务端**（`insurance.service.ts`）⇒ 端侧引用不到，只能自己再写一份
 *    `{ deleted: true; id: string }`。㉝ 批做保单表单时按"删除要回读什么"顺查发现，
 *    已搬进契约层 —— **响应的形状也是契约**，不该只活在服务端。
 */
export interface PolicyDeletedView {
  deleted: true;
  id: string;
}

/** 列表结果 —— **按公司分组**（B 域 PRD §5.5.5「账户分组方案 B」） */
export interface PolicyListResult {
  items: PolicyView[];
  /** 公司名 → 保单数（端侧直接渲染分组头） */
  groupCounts: { company: string; count: number }[];
  /** 全部保单的现金价值合计（**只含计入净资产的**，整数分） */
  countedCashValueTotal: number;
}
